<script lang="ts">
  import { formatDate, formatAmount } from '../../lib/formatters';
  import { getStatusBadgeClass, getStatusLabel } from '../../lib/recurring/transactionGrouping';
  import type { RecurringTransaction } from '../../types/recurring';

  type Props = {
    items: RecurringTransaction[];
  };

  let { items }: Props = $props();
</script>

{#if items.length > 0}
  <div class="upcoming-section">
    <h2>Upcoming Payments</h2>
    <div class="upcoming-list">
      {#each items as item (item.pattern)}
        <div class="recurring-item {getStatusBadgeClass(item.status)}">
          <div class="item-name">{item.displayName}</div>
          <div class="item-date">
            <span class="badge {getStatusBadgeClass(item.status)}">{getStatusLabel(item.daysUntilDue, item.status)}</span>
            <span class="expected-date">{formatDate(item.expectedDate)}</span>
          </div>
          <div class="item-amount">${formatAmount(item.avgAmount)}</div>
        </div>
      {/each}
    </div>
  </div>
{/if}

<style lang="scss">
  .upcoming-section {
    margin-bottom: var(--spacing-xl);

    h2 {
      font-size: var(--font-size-2xl);
      font-weight: var(--font-weight-semibold);
      color: var(--color-text-primary);
      margin: 0 0 var(--spacing-md) 0;
    }
  }

  .upcoming-list {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: var(--spacing-sm);
  }

  .recurring-item {
    display: flex;
    flex-direction: column;
    padding: var(--spacing-sm) var(--spacing-md);
    background: var(--color-background);
    border: var(--border-width-thin) solid var(--color-border);
    border-radius: var(--radius-md);
    transition: all var(--transition-fast);

    &:hover {
      box-shadow: var(--shadow-lg);
    }

    &.status-overdue {
      border-left: var(--border-width-medium) solid var(--color-danger);
    }

    &.status-due-today {
      border-left: var(--border-width-medium) solid var(--color-warning);
    }

    &.status-due-soon {
      border-left: var(--border-width-medium) solid var(--color-warning-border);
    }

    .item-name {
      font-weight: var(--font-weight-semibold);
      color: var(--color-text-primary);
      margin-bottom: var(--spacing-sm);
      font-size: var(--font-size-lg);
    }

    .item-date {
      display: flex;
      align-items: center;
      gap: var(--spacing-sm);
      margin-bottom: var(--spacing-sm);

      .expected-date {
        font-size: var(--font-size-xs);
        color: var(--color-text-tertiary);
      }
    }

    .item-amount {
      font-weight: var(--font-weight-bold);
      font-size: var(--font-size-3xl);
      color: var(--color-text-primary);
    }
  }

  .badge {
    display: inline-block;
    padding: var(--spacing-xs) var(--spacing-md);
    border-radius: var(--radius-xl);
    font-size: var(--font-size-xxs);
    font-weight: var(--font-weight-semibold);

    &.status-due-today {
      background: var(--color-warning-light);
      color: var(--color-warning-dark);
    }

    &.status-due-soon {
      background: var(--color-warning-light);
      color: var(--color-warning-dark);
    }

    &.status-overdue {
      background: var(--color-danger-light);
      color: var(--color-danger-dark);
    }

    &.status-not-due {
      background: var(--color-background-tertiary);
      color: var(--color-text-tertiary);
    }
  }
</style>
